import React from 'react';
import { Card, Space, Tag, Typography, Badge, Button, Divider } from 'antd';
import { 
    ClockCircleOutlined,
    AppstoreOutlined,
    EyeOutlined,
    HeartOutlined,
    HeartFilled,
    CalendarOutlined,
    StarFilled
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import favoriteService from '../../services/favoriteService';
import { message } from 'antd';
import { toggleFavoriteAuction } from '../../redux/userSlice';
import AuctionCountdown from '../Common/AuctionCountdown';
import env from '../../env';

const { Title, Text } = Typography;

const CompactAuctionCard = ({ auction, onFavoriteToggle }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.user);

    const formatDateTime = (dateTime) => {
        if (!dateTime) return 'TBD';
        
        let parsedDate;
        
        if (typeof dateTime === 'string') {
            parsedDate = moment(dateTime);
            
            if (!parsedDate.isValid()) {
                parsedDate = moment(dateTime, 'YYYY-MM-DD HH:mm:ss');
            }
            
            if (!parsedDate.isValid()) {
                parsedDate = moment(dateTime, 'YYYY-MM-DD');
            }
        } else {
            parsedDate = moment(dateTime);
        }
        
        return parsedDate.isValid() ? parsedDate.format('MMM D, h:mm A') : 'Invalid Date';
    };

    const toggleFavorite = async (auctionId, e) => {
        e.stopPropagation();
        if (!currentUser) {
            message.warning('Please login to add favorites');
            return;
        }

        // Call the parent callback if provided
        if (onFavoriteToggle) {
            onFavoriteToggle(auctionId);
        } else {
            // Default behavior - update Redux state
            dispatch(toggleFavoriteAuction(auctionId));
        }

        try {
            const response = await favoriteService.toggleFavoriteAuction(auctionId);
            console.log('Favorite toggle response:', response);
        } catch (error) {
            console.error('Error toggling favorite:', error);
            message.error(error.response?.data?.message || 'Failed to update favorite status');
        }
    };

    return (
        <div style={{ position: 'relative' }}>
            <Card
                hoverable
                onClick={() => navigate(`/auctions/${auction.slug}`)}
                style={{
                    borderRadius: '16px',
                    overflow: 'hidden',
                    height: '100%',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    border: '1px solid #e8eaed',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    position: 'relative'
                }}
                cover={
                    <div style={{ position: 'relative' }}>
                        <div style={{ 
                            position: 'relative',
                            height: 160,
                            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                            overflow: 'hidden'
                        }}>
                            {auction.image ? (
                                <img
                                    alt={auction.title}
                                    src={env.BACKEND_STORAGE_URL + '/' + auction.image}
                                    style={{ 
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        transition: 'transform 0.4s ease'
                                    }}
                                    onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                                    onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                            ) : null}
                            <div
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    display: auction.image ? 'none' : 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                    flexDirection: 'column',
                                    gap: '12px'
                                }}
                            >
                                <div style={{
                                    width: '60px',
                                    height: '60px',
                                    borderRadius: '50%',
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '24px',
                                    fontWeight: 'bold',
                                    backdropFilter: 'blur(10px)'
                                }}>
                                    M
                                </div>
                                <Text style={{ 
                                    fontSize: '12px', 
                                    textAlign: 'center',
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    fontWeight: 500
                                }}>
                                    Maveshibids
                                </Text>
                            </div>
                            
                            {/* Featured badge */}
                            <div style={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                zIndex: 3,
                                background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                                color: '#1f2937',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '10px',
                                fontWeight: 700,
                                boxShadow: '0 2px 6px rgba(255, 215, 0, 0.4)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                backdropFilter: 'blur(10px)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}>
                                <StarFilled style={{ fontSize: '10px' }} />
                                Featured
                            </div>

                            {/* Favorite button */}
                            <Button
                                type="text"
                                icon={auction.is_favorited ? <HeartFilled style={{ color: '#ff4d4f' }} /> : <HeartOutlined />}
                                onClick={(e) => toggleFavorite(auction.id, e)}
                                style={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    zIndex: 3,
                                    background: 'rgba(255, 255, 255, 0.9)',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backdropFilter: 'blur(10px)'
                                }}
                            />

                            {/* Countdown timer in the same place as LIVE NOW */}
                            <AuctionCountdown
                                auction={auction}
                                variant="compact"
                            />
                        </div>
                    </div>
                }
                bodyStyle={{ padding: '16px' }}
            >
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    {/* Category and title */}
                    <div>
                        <Tag color="blue" style={{ 
                            marginBottom: 8,
                            borderRadius: '12px',
                            padding: '2px 8px',
                            fontWeight: 600,
                            fontSize: '10px',
                            border: 'none',
                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                            color: 'white'
                        }}>
                            {auction.category?.name || 'Uncategorized'}
                        </Tag>
                        <Title level={5} style={{ 
                            margin: 0, 
                            fontSize: '16px',
                            color: '#1f2937',
                            fontWeight: 700,
                            lineHeight: 1.3,
                            marginBottom: '8px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {auction.title}
                        </Title>
                    </div>

                    {/* Lots count and start time */}
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        fontSize: '12px'
                    }}>
                        <Space size="small">
                            <AppstoreOutlined style={{ 
                                color: '#1890ff',
                                fontSize: '12px'
                            }} />
                            <Text style={{ 
                                color: '#6b7280',
                                fontSize: '11px'
                            }}>
                                {auction.lots_count || 0} Lots
                            </Text>
                        </Space>
                        <Text style={{ 
                            color: '#6b7280',
                            fontSize: '11px'
                        }}>
                            Started: {formatDateTime(auction.start_time)}
                        </Text>
                    </div>

                    {/* View button */}
                    <Button 
                        type="primary"
                        icon={<EyeOutlined />}
                        onClick={(e) => {
                            e.stopPropagation();
                            const now = moment();
                            const startTime = moment(auction.start_time);
                            const isToday = startTime.isSame(moment(), 'day');
                            
                            if (auction.status === 'completed') {
                                // Auction is completed - show results
                                navigate(`/auctions/${auction.slug}`);
                            } else if (isToday && auction.status === 'active') {
                                // Auction is today and active - open bidding in popup
                                const newWindow = window.open(
                                    `/live-auctions/${auction.slug}`,
                                    '_blank',
                                    'toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=1920,height=1080'
                                );
                                // Focus the new window
                                if (newWindow) {
                                    newWindow.focus();
                                }
                            } else if (startTime.isAfter(now)) {
                                // Auction hasn't started yet - show auction details
                                navigate(`/auctions/${auction.slug}`);
                            } else {
                                // Auction has started - connect to bid
                                navigate(`/auctions/${auction.slug}/bid`);
                            }
                        }}
                        style={{
                            borderRadius: '10px',
                            fontWeight: 600,
                            height: '36px',
                            padding: '0 16px',
                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                            border: 'none',
                            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)',
                            transition: 'all 0.3s ease',
                            fontSize: '12px'
                        }}
                        size="small"
                    >
                        {(() => {
                            const now = moment();
                            const startTime = moment(auction.start_time);
                            const isToday = startTime.isSame(moment(), 'day');
                            
                            if (auction.status === 'completed') {
                                return 'View Auction';
                            } else if (isToday && auction.status === 'active') {
                                return 'Enter Auction';
                            } else if (startTime.isAfter(now)) {
                                return 'View Auction';
                            } else {
                                return 'Enter Auction';
                            }
                        })()}
                    </Button>
                </Space>
            </Card>
        </div>
    );
};

export default CompactAuctionCard; 